/**
 * Kopfkacheln und Befunde des Kapitels „Umsatz & Bezahlqualität" (Persona-Umbau C, 28.10.2026, Plan
 * `.claude/plans/2026-10-28-persona-umbau-paket-1-2.md` § C). Alles beim Rendern aus den API-Antworten der Karten
 * gerechnet — dieselben Felder, die die Karten darunter zeigen, keine zweite Rechnung. Befunde beschreiben, sie nennen
 * keine Ursache; was eine Schätzung ist, sagt das Wort „Schätzung".
 */
import { formatPeriode } from '../../../helpers/auswertungenData';
import { formatPct, quartalLang, TERMS } from '../../../helpers/kpiDefinitions';
import { formatNumber } from '../../../helpers/numberUtils';
import type { QuartalVerlauf } from '../../../helpers/umsatzData';
import { letzterTag } from './teile';

export type Befund = { text: string } | { fehlt: string };
export type Marke = 'up' | 'down' | null;

/** Zweiseitig 5 %: ab diesem |z| trennt der Test den Unterschied vom Zufall. */
export const Z_GRENZE = 1.96;

/**
 * Zwei Zählwerte gleich langer Fenster (Anfos jetzt gegen Anfos im Vorjahresfenster): bedingter Test zweier
 * Poisson-Zahlen, Normalnäherung z = (k1 − k2) ÷ √(k1 + k2). `null` ohne Fälle.
 */
export function zaehlerZ(k1: number, k2: number): number | null {
  return k1 + k2 > 0 ? (k1 - k2) / Math.sqrt(k1 + k2) : null;
}
export const markeAusZ = (z: number | null): Marke => (z === null || Math.abs(z) < Z_GRENZE ? null : z > 0 ? 'up' : 'down');

/**
 * Regel „Basis zu klein" (C2, Jahreszeile 2025 „+465 %"): hat das Vergleichsfenster weniger als ein Drittel der Anfos,
 * misst die Veränderung vor allem die kleine Basis — sie steht dann nicht als Zahl (2024 liegt nur teilweise im
 * Datenbestand und hatte ein anderes Preismodell). Ein Einbruch auf unter ein Drittel bleibt sichtbar.
 */
export const BASIS_MIN_ANTEIL = 1 / 3;
export const basisZuKlein = (jetzt: number, basis: number): boolean => basis > 0 && basis < jetzt * BASIS_MIN_ANTEIL;

/** Ab dieser Abweichung vom Plan (Prozent, beide Richtungen) heißt es „unter/über Plan", darunter „im Plan". */
export const PLAN_TOLERANZ = 1;

/** „−50 %", „+3,6 %", „±0 %" — eine Stelle unter 10 %, sonst ganz (wie `formatDelta`), geschütztes Leerzeichen vor %. */
export function delta(v: number | null | undefined, stellen?: number): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const s = stellen ?? (Math.abs(v) < 10 ? 1 : 0);
  const r = Number(v.toFixed(s));
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${Math.abs(r).toLocaleString('de-DE', { minimumFractionDigits: s, maximumFractionDigits: s })}\u00a0%`;
}
/** Prozentpunkte „−5,1 Pp". */
export function deltaPp(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const r = Number(v.toFixed(1));
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${Math.abs(r).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}\u00a0Pp`;
}
/** Wie `tEuro` (kennzahlTeile): „38,1 T€". */
export const tE = (v: number): string => `${(v / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}\u00a0T€`;
/** „27.10." aus einem exklusiven Datetime-Ende. */
export const bisTag = (bisExkl: string): string => {
  const t = letzterTag(bisExkl);
  return `${t.slice(8, 10)}.${t.slice(5, 7)}.`;
};
/** „4. Q." ohne Jahr. */
const qKurz = (q: string) => formatPeriode(q).replace(/ \d{2}$/, '');

// ─── C1 Kopfkacheln ────────────────────────────────────────────────────────────────────────

export interface QuoteKopf {
  jetzt: number;
  davor: number;
  diffPp: number;
  marke: Marke;
}
/** „31.10.25–31.08.26": Spanne mit zweistelligen Jahren für Kachel-Etiketten (Audit B2, 30.10.2026). */
export const spanneKurz = (erster: string, letzter: string): string => {
  const d = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}`;
  return `${d(erster)}–${d(letzter)}`;
};

/**
 * Etikett der Bezahlquote aus dem echten reifen Fenster (Audit B2): „Anfos 31.10.25–31.08.26, ab 60 Tagen" statt „12 Monate
 * bis …" — die Quote zählt nur Anfos, die heute mindestens `reifeTage` alt sind, die letzten zwei Monate fehlen also.
 * null ohne Fenster (älteres Backend).
 */
export function reifEtikett(w: { erster_tag: string; letzter_tag: string } | null | undefined, reifeTage: number | null | undefined): string | null {
  if (!w) return null;
  return `Anfos ${spanneKurz(w.erster_tag, w.letzter_tag)}, ab ${(reifeTage ?? 60).toLocaleString('de-DE')} Tagen`;
}

type ReifFenster = { erster_tag: string; letzter_tag: string } | null | undefined;

/**
 * Text des Lage-Teasers „Umsatz" auf der Übersicht (Audit B2: dieselben Spannen wie die Kopf-Kachel). Für `kernzahlen.ts`
 * `umsatzKern`: „Bezahlquote, Anfos 31.10.25–31.08.26, ab 60 Tagen · −4,4 Prozentpunkte gegen 28,3 % (31.10.24–31.08.25)".
 * Ohne reife Fenster der frühere Wortlaut. `geprueft = false` hängt „(nicht vom Zufall zu trennen)" an.
 */
export function bezahlquoteKernText(
  kt: { summary: { pay_rate: number | null; pay_rate_davor?: number | null }; reife_tage?: number | null; window_reif?: ReifFenster; window_davor_reif?: ReifFenster },
  geprueft: boolean,
): string | null {
  const s = kt.summary;
  const davor = s.pay_rate_davor ?? null;
  if (s.pay_rate === null || davor === null) return null;
  const pp = s.pay_rate - davor;
  const r = Number(pp.toFixed(1));
  const ppLang = `${r > 0 ? '+' : r < 0 ? '−' : '±'}${formatNumber(Math.abs(r), 1)} Prozentpunkte`;
  const jetzt = reifEtikett(kt.window_reif, kt.reife_tage);
  const zufall = geprueft ? '' : ' (nicht vom Zufall zu trennen)';
  if (!jetzt || !kt.window_davor_reif) {
    return `Bezahlquote der letzten 12 Monate (Anfos ab ${kt.reife_tage ?? 60} Tagen) · ${ppLang} gegen ${formatPct(davor)} ein Jahr früher${zufall}`;
  }
  return `${TERMS.bezahlquote.label}, ${jetzt} · ${ppLang} gegen ${formatPct(davor)} (${spanneKurz(kt.window_davor_reif.erster_tag, kt.window_davor_reif.letzter_tag)})${zufall}`;
}

/** Bezahlquote „Alle Anfos" der Karte „Wer bestellt — und wer zahlt" (reife Spanne der 12 Monate gegen dieselbe ein Jahr früher). */
export function bezahlquoteKopf(sm: { pay_rate: number | null; pay_rate_davor?: number | null; test?: { flag: Marke } | null } | null | undefined): QuoteKopf | null {
  if (!sm || sm.pay_rate == null || sm.pay_rate_davor == null) return null;
  return { jetzt: sm.pay_rate, davor: sm.pay_rate_davor, diffPp: sm.pay_rate - sm.pay_rate_davor, marke: sm.test?.flag ?? null };
}

export interface AnfosKopf {
  quarter: string;
  laufend: boolean;
  jetzt: number;
  basis: number;
  /** Veränderung der Anfos in Prozent (`veraenderung.delta_anfos`). */
  delta: number | null;
  marke: Marke;
  /** „4. Q. bis 27.10. gegen 4. Q. 25 bis 27.10." */
  fenster: string;
}
/** Laufendes (sonst jüngstes) Bestellquartal gegen dasselbe Fenster ein Jahr früher — aus `quartale_verlauf`. */
export function anfosKopf(qs: QuartalVerlauf[] | undefined): AnfosKopf | null {
  const q = (qs ?? []).filter((x) => x.veraenderung).slice(-1)[0];
  if (!q || !q.veraenderung) return null;
  const [j, n] = q.quarter.split('-Q');
  const vorjahr = `${Number(j) - 1}-Q${n}`;
  const bis = q.laufend ? ` bis ${bisTag(q.fenster.bis)}` : '';
  return {
    quarter: q.quarter,
    laufend: q.laufend,
    jetzt: q.anfos,
    basis: q.veraenderung.basis.anfos,
    delta: q.veraenderung.delta_anfos,
    marke: markeAusZ(zaehlerZ(q.anfos, q.veraenderung.basis.anfos)),
    fenster: `${qKurz(q.quarter)}${bis} gegen ${formatPeriode(vorjahr)}${bis}`,
  };
}

export function planStatus(drift: number | null | undefined): { text: string; ton: 'gut' | 'schlecht' | 'neutral' } | null {
  if (drift === null || drift === undefined) return null;
  if (drift <= -PLAN_TOLERANZ) return { text: 'unter Plan', ton: 'schlecht' };
  if (drift >= PLAN_TOLERANZ) return { text: 'über Plan', ton: 'gut' };
  return { text: 'im Plan', ton: 'neutral' };
}

// ─── C2 Quartale gegen das Vorjahresquartal ─────────────────────────────────────────────────

/** Jüngstes Quartal mit Vorjahr, dazu die zwei davor; Quartale mit zu kleiner Basis werden genannt, nicht gerechnet. */
export function quartalBefund(qs: QuartalVerlauf[] | undefined): Befund {
  const mit = (qs ?? []).filter((q) => q.veraenderung && q.veraenderung.delta !== null);
  const q = mit[mit.length - 1];
  if (!q || !q.veraenderung) return { fehlt: 'Noch kein Bestellquartal mit Vorjahresquartal im Datenbestand.' };
  const v = q.veraenderung;
  const [j, n] = q.quarter.split('-Q');
  const bis = q.laufend ? ` bis ${bisTag(q.fenster.bis)}` : '';
  // Audit C7: im Befund ausgeschrieben („4. Quartal 2026"), an Achsen und Etiketten kurz.
  const kopf = `${quartalLang(q.quarter)}${bis}: ${TERMS.wertBeiBestellung.short} ${delta(v.delta)} gegen ${quartalLang(`${Number(j) - 1}-Q${n}`)}${bis}`;
  const zerlegung = `(${TERMS.anfos.label} ${delta(v.delta_anfos)}, ${q.anfos.toLocaleString('de-DE')} gegen ${v.basis.anfos.toLocaleString('de-DE')}; Ø je Anfo ${delta(v.delta_je_anfo)})`;
  const davor = mit
    .slice(0, -1)
    .slice(-2)
    .reverse()
    .map((x) => (basisZuKlein(x.anfos, x.veraenderung?.basis.anfos ?? 0) ? `${quartalLang(x.quarter)} Basis zu klein` : `${quartalLang(x.quarter)} ${delta(x.veraenderung?.delta)}`));
  return { text: `${kopf} ${zerlegung}${davor.length ? `. Davor: ${davor.join(', ')}` : ''}.` };
}

// ─── C3 Rechnungsstufen ─────────────────────────────────────────────────────────────────────

export type VerlustStufe = 'no_invoice_ended' | 'invoiced_unpaid' | 'trial_only';
export const VERLUST_STUFEN: { key: VerlustStufe; term: { label: string } }[] = [
  { key: 'no_invoice_ended', term: TERMS.stufeVorErsterRechnung },
  { key: 'invoiced_unpaid', term: TERMS.stufeRechnungOffen },
  { key: 'trial_only', term: TERMS.stufeNurTestphase },
];
export interface StufenQuartal {
  quarter?: string;
  anfos: number;
  provisional: number;
  is_young?: boolean;
  no_invoice_ended: number;
  invoiced_unpaid: number;
  trial_only: number;
  value_no_invoice_ended?: number;
  value_invoiced_unpaid?: number;
  value_trial_only?: number;
}
const spanneText = (xs: number[]) => {
  const lo = Math.min(...xs);
  const hi = Math.max(...xs);
  return Math.round(lo) === Math.round(hi) ? formatPct(lo, 0) : `${formatNumber(lo, 0)}–${formatPct(hi, 0)}`;
};

/** Größte Verluststufe über die reifen Quartale (Summe der Anfos), Spanne je Quartal und ihr Erwarteter Wert. */
export function groessterVerlust(quartale: StufenQuartal[]): { key: VerlustStufe; befund: Befund } {
  const reif = quartale.filter((q) => !q.is_young && q.anfos - q.provisional > 0);
  if (reif.length === 0) return { key: 'no_invoice_ended', befund: { fehlt: 'Noch kein reifes Bestellquartal: die Rechnungsstufen stehen noch nicht fest.' } };
  const summe = (k: VerlustStufe) => reif.reduce((s, q) => s + q[k], 0);
  const [erste, ...rest] = [...VERLUST_STUFEN].sort((a, b) => summe(b.key) - summe(a.key));
  const anteile = (k: VerlustStufe) => reif.map((q) => (q[k] / (q.anfos - q.provisional)) * 100);
  const von = reif[0].quarter ?? '';
  const bis = reif[reif.length - 1].quarter ?? '';
  const zeitraum = von && bis ? ` (${quartalLang(von)} – ${quartalLang(bis)})` : '';
  const text =
    `Größter Verlust: „${erste.term.label}“ — ${spanneText(anteile(erste.key))} der Anfos je reifem Quartal${zeitraum}, ` +
    `mehr als ${rest.map((r) => `„${r.term.label}“ (${spanneText(anteile(r.key))})`).join(' und ')}.`;
  // Kein Euro (Runde 2, GF): der Erwartete Wert dieser Anfos (≈ 320 T€) las sich als Verlust, ist aber schon eingepreist —
  // der Erwartete Wert rechnet mit ein, dass viele Anfos nie zahlen. Die Wahlspalte „Wert der Stufe" bleibt mit Tooltip.
  return { key: erste.key, befund: { text } };
}

// ─── C5 Wer bestellt — und wer zahlt ────────────────────────────────────────────────────────

export interface KundentypBefundEingang {
  alle: { pay_rate: number | null; pay_rate_davor?: number | null; test?: { flag: Marke } | null };
  typen: { name: string; pay_rate: number | null; pay_rate_davor?: number | null; test?: { flag: Marke } | null }[];
  mischung_quote: number | null | undefined;
  /** Reife Spanne ein Jahr früher, „31.10.2024–31.08.2025" (Plan 2026-10-30-vergleichsfenster-drift). */
  fensterDavor: string | null;
}
const pfeil = (m: Marke | undefined) => (m === 'down' ? ' ▼' : m === 'up' ? ' ▲' : '');

/** Lesezeile der Karte als Befund: Alle davor → jetzt, je Typ, und was dieselbe Mischung wie vor einem Jahr ergäbe. */
export function kundentypBefund(e: KundentypBefundEingang): Befund {
  const a = e.alle;
  if (a.pay_rate == null || a.pay_rate_davor == null) return { fehlt: 'Für dieselbe Spanne ein Jahr früher liegt keine Bezahlquote vor.' };
  const p = (x: number | null | undefined, y: number | null | undefined) => `${formatPct(x ?? null)} → ${formatPct(y ?? null)}`;
  const kopf = `${TERMS.bezahlquote.label} aller Anfos ${p(a.pay_rate_davor, a.pay_rate)}${pfeil(a.test?.flag)} gegen dieselbe Spanne ein Jahr früher${e.fensterDavor ? ` (${e.fensterDavor})` : ''}`;
  const typen = e.typen.map((t) => `${t.name} ${p(t.pay_rate_davor, t.pay_rate)}${pfeil(t.test?.flag)}`).join(' · ');
  let mischung = '';
  const gesamt = a.pay_rate - a.pay_rate_davor;
  if (e.mischung_quote != null && Math.abs(gesamt) >= 0.05) {
    const anteil = (a.pay_rate - e.mischung_quote) / gesamt;
    const richtung = gesamt < 0 ? 'Rückgang' : 'Anstieg';
    const urteil =
      Math.abs(anteil) < 0.25
        ? `Der ${richtung} steckt in den Gruppen selbst, nicht in der Mischung.`
        : Math.abs(anteil) > 0.75
          ? `Der ${richtung} kommt vor allem aus der Mischung.`
          : `Zum ${richtung} tragen Gruppen und Mischung beide bei.`;
    mischung = ` Mit derselben Mischung aus Erstbestellern, früheren Zahlern und Nie-Zahlern wie vor einem Jahr läge die Quote bei ${formatPct(e.mischung_quote)}. ${urteil}`;
  }
  return { text: `${kopf}. ${typen}.${mischung}` };
}

// ─── C8 Neue Leads je Quartal ───────────────────────────────────────────────────────────────

export interface LeadQuartal {
  quarter: string;
  leads: number;
  rate90: number | null;
}
const tsd = (n: number) => (n >= 10_000 ? `${Math.round(n / 1000).toLocaleString('de-DE')} Tsd.` : `${(n / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} Tsd.`);

/**
 * Rückgang der neuen Leads über die vollen Quartale (das laufende zählt nicht mit): jüngstes volles Quartal gegen das
 * davor und gegen den Höchststand der Reihe, dazu die Spanne der 90-Tage-Quote. Kein Vergleich zum Vorjahr (F8, Till
 * 23.10.2026: erst nach Klärung des Rückgangs).
 */
export function leadBefund(quartale: LeadQuartal[] | undefined, laufendAb: string | null): Befund {
  const voll = (quartale ?? []).filter((q) => !laufendAb || q.quarter < laufendAb);
  if (voll.length < 2) return { fehlt: 'Zu wenige volle Quartale für einen Verlauf.' };
  const letzt = voll[voll.length - 1];
  const vor = voll[voll.length - 2];
  const hoch = voll.reduce((m, q) => (q.leads > m.leads ? q : m), voll[0]);
  const reihe = voll.slice(-4).map((q) => `${quartalLang(q.quarter)} ${tsd(q.leads)}`).join(' → ');
  const gegenHoch = hoch.quarter !== letzt.quarter && hoch.leads > 0 ? `, ${delta(((letzt.leads / hoch.leads) - 1) * 100)} gegen den Höchststand (${quartalLang(hoch.quarter)})` : '';
  const quoten = voll.map((q) => q.rate90).filter((r): r is number => r !== null);
  const quote = quoten.length >= 2 ? ` Der Anteil mit Anfo binnen 90 Tagen lag dabei zwischen ${formatPct(Math.min(...quoten), 2)} und ${formatPct(Math.max(...quoten), 2)}.` : '';
  const vorDelta = vor.leads > 0 ? delta((letzt.leads / vor.leads - 1) * 100) : '—';
  return {
    text: `Neue Leads je Quartal: ${reihe} — ${quartalLang(letzt.quarter)}: ${vorDelta} gegen das Quartal davor${gegenHoch}.${quote}${letzt.leads < hoch.leads ? ' Die Ursache des Rückgangs wird geprüft.' : ''}`,
  };
}
